import React from 'react';

import { RHFTextInput } from '@cowbell/ui';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useFormContext } from 'react-hook-form';

import { validateDomainName } from '../../../../accounts/AccountService';

interface OrgDomainFieldProps {
  readonly name: string;
  readonly label: string;
  readonly placeholder: string;
  /** Secondary domains are entered as one comma-separated field. */
  readonly isMulti?: boolean;
}

const stripProtocol = (value: string) =>
  value.replace(/^https?:\/\//, '').replace(/^www\./, '');

/** The first domain of a comma-separated list, which is the one worth checking. */
const firstDomain = (value: string) =>
  stripProtocol(value.split(',')[0]?.trim() ?? '');

/**
 * A web domain, in @cowbell/ui components, keeping the console's existing
 * `validateDomainName` round trip behind the valid/invalid mark.
 *
 * <p>The check is advisory: it marks the field, it does not block the form.
 * That matches the MUI field this replaces.
 */
export const OrgDomainField = ({
  name,
  label,
  placeholder,
  isMulti = false,
}: OrgDomainFieldProps) => {
  const { watch } = useFormContext();
  const value = watch(name) as string | undefined;
  const [status, setStatus] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    const domain = firstDomain(value ?? '');

    if (!domain) {
      setStatus(null);
      return undefined;
    }

    let live = true;
    const timer = window.setTimeout(() => {
      validateDomainName({ domain })
        .then(() => {
          if (live) setStatus(true);
        })
        .catch(() => {
          if (live) setStatus(false);
        });
    }, 500);

    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [value]);

  return (
    <RHFTextInput
      name={name}
      label={label}
      placeholder={placeholder}
      inputMode="url"
      aria-describedby={status === false ? `${name}-domain-status` : undefined}
      rightSection={
        <DomainStatus name={name} status={status} multi={isMulti} />
      }
    />
  );
};

function DomainStatus({
  name,
  status,
  multi,
}: Readonly<{ name: string; status: boolean | null; multi: boolean }>) {
  if (status === null) {
    return null;
  }

  const label = multi ? 'First domain' : 'Domain';

  if (status) {
    return (
      <CheckCircleIcon
        fontSize="inherit"
        color="success"
        titleAccess={`${label} resolves`}
      />
    );
  }

  return (
    <CancelIcon
      id={`${name}-domain-status`}
      fontSize="inherit"
      color="error"
      titleAccess={`${label} does not resolve`}
    />
  );
}

export default OrgDomainField;
